/**
 * Pure decision logic behind `MergeMemberModal`.
 *
 * Lives apart from the component for one reason: `merge_member` takes two ids
 * that look interchangeable and are not — one of the two profiles is closed
 * and cannot be recovered. Getting the direction backwards is the single worst
 * thing this control can do, and the SPA tree carries no component tests (it
 * is ESLint-ignored and coverage-excluded), so the part worth proving is
 * pulled out to where it can be proven.
 */

/** Money/schedule rows on the profile the modal was opened from. */
export interface MergeMemberCounts {
  readonly bookings: number;
  readonly payments: number;
  readonly packages: number;
  readonly memberships: number;
}

/** The wire shape `merge_member` expects: `from` is absorbed, `to` survives. */
export interface MergeDirection {
  readonly from_member_id: string;
  readonly to_member_id: string;
}

/**
 * Map "which profile do we keep" onto the action's absorb/survive pair.
 *
 * `keepThis === false` is the default and the common case: staff open a
 * placeholder minted by a first Instagram DM or WhatsApp message and fold it
 * into the real account, so THIS profile is the one absorbed.
 */
export function mergeDirection(args: {
  thisMemberId: string;
  otherMemberId: string;
  keepThis: boolean;
}): MergeDirection {
  return args.keepThis
    ? { from_member_id: args.otherMemberId, to_member_id: args.thisMemberId }
    : { from_member_id: args.thisMemberId, to_member_id: args.otherMemberId };
}

/**
 * "2 bookings, 1 payment and 3 packages" — zero rows are omitted so the
 * sentence only ever mentions things that actually exist. Empty string when
 * there is nothing to move, which is the case the caller wants to stay silent
 * about (no warning is better than "this profile has nothing").
 */
export function describeMergeCounts(counts: MergeMemberCounts): string {
  const parts: string[] = [];
  const push = (n: number, one: string, many: string): void => {
    if (n > 0) parts.push(`${n} ${n === 1 ? one : many}`);
  };
  push(counts.bookings, "booking", "bookings");
  push(counts.payments, "payment", "payments");
  push(counts.packages, "package", "packages");
  push(counts.memberships, "membership", "memberships");
  if (parts.length === 0) return "";
  const last = parts[parts.length - 1] as string;
  if (parts.length === 1) return last;
  return `${parts.slice(0, -1).join(", ")} and ${last}`;
}

/** Row counts returned by a successful `merge_member` dispatch. */
export interface MergeMovedCounts {
  readonly bookings_moved?: number;
  readonly payments_moved?: number;
  readonly packages_moved?: number;
  readonly memberships_moved?: number;
  readonly checkouts_moved?: number;
  readonly flows_moved?: number;
  /** How many of the moved flows were RUNNING and were ended by the merge. */
  readonly flows_ended?: number;
  /**
   * The other six kinds the preview lists (merge-line-counts-every-kind,
   * 2026-10-07): the duplicate's rows now on the kept profile, which is the
   * number the preview showed.
   */
  readonly tags_moved?: number;
  readonly conversations_moved?: number;
  readonly vendor_links_moved?: number;
  readonly channel_bindings_moved?: number;
  readonly session_notes_moved?: number;
  readonly session_feedback_moved?: number;
  /** WhatsApp consultation-form photos (`picture_intakes`, migration 227). */
  readonly picture_intakes_moved?: number;
  /** Identity metadata keys the survivor inherited, e.g. `instagram_handle`. */
  readonly identity_keys_moved?: ReadonlyArray<string>;
  /**
   * The onboarding / consultation QUESTIONS whose answers the survivor
   * inherited ("Activities", "Focus areas" …) — labels, never an answer.
   */
  readonly answers_moved?: ReadonlyArray<string>;
  /** Questions both profiles answered differently: the survivor's answer stood. */
  readonly answer_conflicts?: ReadonlyArray<string>;
  /**
   * `members` identity COLUMNS the survivor inherited — `phone` / `email` /
   * `display_name`. Reported separately from the metadata keys because they are
   * what staff actually check afterwards, and their silent loss is the defect
   * this whole change exists to close.
   */
  readonly identity_columns_moved?: ReadonlyArray<string>;
  /** Columns deliberately refused, with a stable reason code. */
  readonly identity_columns_skipped?: ReadonlyArray<{
    readonly column: string;
    readonly reason: string;
  }>;
  /**
   * Ad attribution (merge-attribution-first-known, 2026-10-14): whether the
   * closing profile's earlier date became the survivor's first record, how
   * many of its visit records (recorded touches — ad clicks, but also organic
   * and newsletter visits) the survivor inherited, and the ad tags (slugs) the
   * merge took off because the combined history shows them wrong.
   */
  readonly first_known_moved?: boolean;
  readonly attribution_touches_moved?: number;
  readonly ad_tags_removed?: ReadonlyArray<string>;
  /**
   * The closing profile's opt-outs the survivor now holds as well
   * (merge-carries-opt-outs, 2026-10-19) — key names such as
   * `flow_opt_outs.newsletter` or `email_opt_in`, never a value.
   */
  readonly opt_outs_carried?: ReadonlyArray<string>;
}

/** `instagram_handle` → "Instagram handle"; unknown keys degrade readably. */
function identityKeyLabel(key: string): string {
  if (key === "instagram_handle") return "Instagram handle";
  return key.replace(/_/g, " ");
}

/** `display_name` → "Name". Used by both the preview table and the result line. */
export function identityColumnLabel(column: string): string {
  if (column === "display_name") return "Name";
  if (column === "phone") return "Phone";
  if (column === "email") return "Email";
  return column.replace(/_/g, " ");
}

/**
 * The success line. Reports what the ACTION said it moved rather than what the
 * page predicted, so a merge that touched more than the page knew about (a row
 * created between load and confirm) is reported honestly.
 */
export function describeMergeResult(
  moved: MergeMovedCounts,
  survivorLabel: string,
): string {
  const rows: ReadonlyArray<readonly [number, string, string]> = [
    [moved.bookings_moved ?? 0, "booking", "bookings"],
    [moved.payments_moved ?? 0, "payment", "payments"],
    [moved.packages_moved ?? 0, "package", "packages"],
    [moved.memberships_moved ?? 0, "membership", "memberships"],
    [moved.checkouts_moved ?? 0, "checkout", "checkouts"],
    [moved.flows_moved ?? 0, "flow", "flows"],
    // The rest of what the preview lists, in its order and with its nouns, so
    // a merge of tags or a chat alone no longer reads "Nothing to move."
    [moved.tags_moved ?? 0, "tag", "tags"],
    [moved.conversations_moved ?? 0, "conversation", "conversations"],
    [moved.vendor_links_moved ?? 0, "linked account", "linked accounts"],
    [moved.channel_bindings_moved ?? 0, "chat number", "chat numbers"],
    [moved.session_notes_moved ?? 0, "session note", "session notes"],
    [moved.session_feedback_moved ?? 0, "feedback entry", "feedback entries"],
    [moved.picture_intakes_moved ?? 0, "consultation form photo", "consultation form photos"],
  ];
  const detail = rows
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`)
    .join(", ");
  const target = survivorLabel === "" ? "the other profile" : survivorLabel;
  const flowsEnded = describeFlowsEnded({
    count: moved.flows_ended,
    tense: "result",
    survivorLabel,
  });
  const flowsEndedLine = flowsEnded === "" ? "" : ` ${flowsEnded}`;
  // Identity keys are reported separately from the row counts because they are
  // a different kind of thing: nothing was RE-PARENTED, an identifier was
  // carried across so the survivor can be recognised by it next time. Worth
  // saying out loud — it is the difference between a merge that holds and one
  // whose duplicate walks back in.
  const carried = [
    ...(moved.identity_columns_moved ?? []).map(identityColumnLabel),
    ...(moved.identity_keys_moved ?? []).map(identityKeyLabel),
  ];
  const carriedLine =
    carried.length === 0 ? "" : ` Carried over the ${joinWithAnd(carried)}.`;
  // The answers (onboarding, consultation) are a third kind of carry — named by
  // question, never by value — and the merge's survivor-wins conflicts are said
  // aloud for the same reason a refused column is: silence reads as a clean merge.
  const answers = describeAnswerCarry({
    carried: moved.answers_moved,
    conflicts: moved.answer_conflicts,
    tense: "result",
    survivorLabel,
  });
  const answersLine = `${answers.carried === "" ? "" : ` ${answers.carried}`}${
    answers.kept === "" ? "" : ` ${answers.kept}`
  }`;
  // Ad attribution is said aloud for the same reason: a tag the merge took off
  // would otherwise just vanish from the profile.
  const adAttribution = describeAdAttributionCarry({
    firstKnownMoved: moved.first_known_moved,
    touchesMoved: moved.attribution_touches_moved,
    adTagsRemoved: moved.ad_tags_removed,
    tense: "result",
    survivorLabel,
  });
  const adAttributionLine = adAttribution === "" ? "" : ` ${adAttribution}`;
  // Opt-outs too: a merge that kept someone's "no" should say so.
  const optOuts = describeOptOutCarry({
    carried: moved.opt_outs_carried,
    tense: "result",
    survivorLabel,
  });
  const optOutsLine = optOuts === "" ? "" : ` ${optOuts}`;
  // A refused column is reported too. Silence here is exactly what let the
  // 2026-09-04 phone loss pass as a clean merge.
  const skipped = (moved.identity_columns_skipped ?? []).map(
    (s) =>
      `${identityColumnLabel(s.column).toLowerCase()} (${
        skipReasonNote(s.reason) ?? "not carried"
      })`,
  );
  const skippedLine =
    skipped.length === 0 ? "" : ` Did not carry the ${joinWithAnd(skipped)}.`;
  return detail === ""
    ? `Merged into ${target}. Nothing to move.${carriedLine}${answersLine}${adAttributionLine}${optOutsLine}${skippedLine}`
    : `Merged into ${target}. Moved ${detail}.${flowsEndedLine}${carriedLine}${answersLine}${adAttributionLine}${optOutsLine}${skippedLine}`;
}

/**
 * The sentence about RUNNING marketing flows, shared by the preview (what WILL
 * happen) and the result line (what DID) so they read alike. A run is for the
 * member it was started for, so the merge ends it instead of carrying it on
 * for the kept profile: the row crosses as history and the kept profile can be
 * enrolled again. Empty when nothing was running. The count is the server's
 * (`flows_ending` from the preview, `flows_ended` from the merge), never
 * worked out here.
 */
export function describeFlowsEnded(args: {
  readonly count: number | undefined;
  readonly tense: "preview" | "result";
  /** Who the flow does not carry on for — read in the preview's sentence. */
  readonly survivorLabel: string;
}): string {
  const n = args.count ?? 0;
  if (n <= 0) return "";
  const one = n === 1;
  if (args.tense === "result") {
    return `Ended ${n} running ${one ? "flow" : "flows"}.`;
  }
  const who = args.survivorLabel === "" ? "the other profile" : args.survivorLabel;
  return `${n} running marketing ${one ? "flow" : "flows"} will end: ${
    one ? "it was" : "they were"
  } started for the closing profile and ${one ? "does" : "do"} not carry on for ${who}.`;
}

/** "Trainer gender preference" → "trainer gender preference" — read mid-sentence. */
function answerLabelText(label: string): string {
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/**
 * The two answer sentences, shared by the preview (what WILL happen) and the
 * result line (what DID) so they read alike. `carried` says what the kept
 * profile inherits; `kept` names the questions both profiles answered
 * differently — the kept profile's own answer stands and the other one goes
 * with the closed profile. Question labels only: the server never sends an
 * answer, and nothing here could show one.
 */
export function describeAnswerCarry(args: {
  readonly carried: ReadonlyArray<string> | undefined;
  readonly conflicts: ReadonlyArray<string> | undefined;
  readonly tense: "preview" | "result";
  /** Who keeps the answers — read in the preview's sentence. */
  readonly survivorLabel: string;
}): { readonly carried: string; readonly kept: string } {
  const carriedLabels = (args.carried ?? []).map(answerLabelText);
  const keptLabels = (args.conflicts ?? []).map(answerLabelText);
  const preview = args.tense === "preview";
  const who = args.survivorLabel === "" ? "The other profile" : args.survivorLabel;
  return {
    carried:
      carriedLabels.length === 0
        ? ""
        : preview
          ? `${who} will inherit the answers for ${joinWithAnd(carriedLabels)}.`
          : `Carried over the answers for ${joinWithAnd(carriedLabels)}.`,
    kept:
      keptLabels.length === 0
        ? ""
        : preview
          ? `Both profiles answered ${joinWithAnd(keptLabels)} differently: ${who}'s own answer is kept and the closing profile's is discarded.`
          : `Kept the existing answers for ${joinWithAnd(keptLabels)}; the other profile answered differently.`,
  };
}

/**
 * An opt-out key as staff read it mid-sentence: `flow_opt_outs.all_marketing`
 * → "all marketing", `email_opt_in` → "contact by email". An unknown flow
 * key degrades readably.
 */
export function optOutLabel(key: string): string {
  if (key === "flow_opt_outs.all_marketing") return "all marketing";
  if (key === "flow_opt_outs.newsletter") return "the newsletter";
  if (key === "email_opt_in") return "contact by email";
  if (key === "whatsapp_opt_in") return "contact by WhatsApp";
  if (key === "consent.marketing") return "marketing on the website";
  // session-feedback-cadence (2026-10-21): the Session feedback switch.
  if (key === "session_feedback_opt_in") return "session feedback requests";
  if (key.startsWith("flow_opt_outs.")) {
    return `the ${key.slice("flow_opt_outs.".length).replace(/_/g, " ")} flow`;
  }
  return key.replace(/[._]/g, " ");
}

/**
 * The opt-out sentence, shared by the preview (what WILL happen) and the
 * result line (what DID) so they read alike (merge-carries-opt-outs,
 * 2026-10-19). A merge says the two profiles are one person, so every opt-out
 * the closing profile held stays on the kept one — even where the kept
 * profile had said yes (Mario: "Every opt-out"). Key names come from the
 * server; empty when nothing carries.
 */
export function describeOptOutCarry(args: {
  readonly carried: ReadonlyArray<string> | undefined;
  readonly tense: "preview" | "result";
  /** Who stays opted out — read in the preview's sentence. */
  readonly survivorLabel: string;
}): string {
  const labels = (args.carried ?? []).map(optOutLabel);
  if (labels.length === 0) return "";
  if (args.tense === "result") {
    return `Kept the closed profile's opt-outs: ${joinWithAnd(labels)}.`;
  }
  const who = args.survivorLabel === "" ? "The other profile" : args.survivorLabel;
  return `${who} will be opted out of ${joinWithAnd(labels)}, as the closing profile was: a merge keeps every opt-out.`;
}

/**
 * The ad tags a merge can take off, as the tags catalog labels them — a mirror
 * of `ATTRIBUTION_TAG_LABELS` over `ABSORB_RECHECKED_ATTRIBUTION_SLUGS` in
 * `@layer/canonical-actions`, which a browser bundle does not import
 * (`merge-member-direction.test.ts` pins the two together).
 */
const AD_TAG_LABELS: Readonly<Record<string, string>> = {
  "source-google-ads": "Source: Google Ads",
  "source-meta-ads": "Source: Meta Ads",
  "first-purchase-google-ads": "First purchase: Google Ads",
  "first-purchase-meta-ads": "First purchase: Meta Ads",
};

/** `source-google-ads` → "Source: Google Ads"; an unknown slug degrades readably. */
export function adTagLabel(slug: string): string {
  return AD_TAG_LABELS[slug] ?? slug.replace(/-/g, " ");
}

/**
 * The ad-attribution sentences, shared by the preview (what WILL happen) and
 * the result line (what DID) so they read alike
 * (merge-attribution-first-known, 2026-10-14). An ad tag says whether an ad
 * found the person, judged against when Clavis first knew them, so a merge
 * carries the closing profile's visit records — its recorded touches, each
 * the source of a site visit that sent a form or a sign-up, ad clicks among
 * them — and, when it is the older record, its earlier date as the kept
 * profile's first record. It then takes off a "Source:" or "First purchase:"
 * ad tag that the combined history shows was wrong. Everything here is the
 * server's: the preview cannot name a removal (that is judged on the merged
 * history), so only the result line does. Empty when there is nothing to say.
 *
 * The date sentence claims only what the merge does — the earlier record is
 * kept as the first record — and never that the tags are now timed from it:
 * when the kept profile's first purchase is earlier still, that purchase is
 * what times them (the pre-push review, 2026-10-14).
 */
export function describeAdAttributionCarry(args: {
  readonly firstKnownMoved: boolean | undefined;
  readonly touchesMoved: number | undefined;
  /** Result only: the ad tags (slugs) the merge took off the kept profile. */
  readonly adTagsRemoved?: ReadonlyArray<string> | undefined;
  readonly tense: "preview" | "result";
  /** Who inherits the records — read in the preview's sentences. */
  readonly survivorLabel: string;
}): string {
  const preview = args.tense === "preview";
  const who = args.survivorLabel === "" ? "the other profile" : args.survivorLabel;
  const sentences: string[] = [];
  const n = args.touchesMoved ?? 0;
  if (n > 0) {
    const records = n === 1 ? "visit record" : "visit records";
    sentences.push(
      preview ? `${n} ${records} will move to ${who}.` : `Carried over ${n} ${records}.`,
    );
  }
  if (args.firstKnownMoved === true) {
    sentences.push(
      preview
        ? `The closing profile is the older record, so its date will be kept as ${who}'s first record.`
        : `Kept the closed profile's earlier date as ${who}'s first record.`,
    );
  }
  const removed = preview ? [] : (args.adTagsRemoved ?? []).map(adTagLabel);
  if (removed.length > 0) {
    sentences.push(`Took off ${joinWithAnd(removed)}, which the combined history no longer supports.`);
  }
  return sentences.join(" ");
}

/** "a", "a and b", "a, b and c". */
function joinWithAnd(parts: ReadonlyArray<string>): string {
  if (parts.length <= 1) return parts[0] ?? "";
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1] as string}`;
}

// ---------------------------------------------------------------------------
// Merge PREVIEW — the side-by-side shown BEFORE the irreversible confirm.
// ---------------------------------------------------------------------------

/**
 * The preview exists because the merge's most consequential effect was also its
 * least visible one. Row counts were shown; IDENTITY was not — so a merge that
 * silently dropped a phone number looked exactly like a merge that did not
 * (2026-09-04). Everything below is presentation only: every verdict and every
 * warning code is decided server-side by `preview_member_merge`, which shares
 * its rules with the merge itself. Nothing here re-derives a decision, because a
 * preview that disagrees with the merge is worse than no preview at all.
 */

/** One member's identity, as `preview_member_merge` returns it. */
export interface PreviewProfile {
  readonly member_id: string;
  readonly display_name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly status: string;
  readonly instagram_handle: string | null;
  readonly email_is_placeholder: boolean;
  readonly created_at: string;
}

export interface PreviewIdentityVerdict {
  readonly column: string;
  readonly decision: "carry" | "skip";
  readonly reason: string | null;
}

export interface PreviewMoveCounts {
  readonly bookings: number;
  readonly payments: number;
  readonly packages: number;
  readonly memberships: number;
  readonly checkouts: number;
  readonly flows: number;
  readonly tags: number;
  readonly conversations: number;
  readonly vendor_links: number;
  readonly channel_bindings: number;
  readonly session_notes: number;
  readonly session_feedback: number;
  readonly picture_intakes: number;
}

export interface MergePreview {
  readonly absorbed: PreviewProfile;
  readonly survivor: PreviewProfile;
  readonly identity: ReadonlyArray<PreviewIdentityVerdict>;
  readonly metadata_keys_carried: ReadonlyArray<string>;
  /** Questions whose answers the kept profile would inherit — labels only. */
  readonly answers_carried: ReadonlyArray<string>;
  /** Questions both answered differently; the kept profile's answer stands — labels only. */
  readonly answer_conflicts: ReadonlyArray<string>;
  readonly moves: PreviewMoveCounts;
  /** How many of `moves.flows` are running; the merge ends those. */
  readonly flows_ending: number;
  /** The closing profile is the older record, so its date becomes the kept profile's first record. */
  readonly first_known_moves: boolean;
  /** How many of the closing profile's recorded touches (visit records) the kept profile gains. */
  readonly attribution_touches_carried: number;
  /** The closing profile's opt-outs the kept profile would hold as well (key names). */
  readonly opt_outs_carried: ReadonlyArray<string>;
  readonly warnings: ReadonlyArray<string>;
}

/** A row of the side-by-side identity table. */
export interface PreviewIdentityRow {
  readonly column: string;
  readonly label: string;
  /** The closing profile's value, or null when it has none. */
  readonly absorbedValue: string | null;
  /** The surviving profile's value, or null when it has none. */
  readonly survivorValue: string | null;
  /** What the survivor ends up with once the merge runs. */
  readonly resultValue: string | null;
  /**
   * `moves`   — the survivor gains this value.
   * `keeps`   — the survivor already had one; the absorbed value is discarded.
   * `lost`    — the absorbed profile had a value, the survivor has none, and it
   *             will NOT be carried. This is the state that caused the incident,
   *             and the row the operator most needs to see.
   * `neither` — nobody had a value.
   */
  readonly effect: "moves" | "keeps" | "lost" | "neither";
  /** Short human explanation for a `lost` row; null otherwise. */
  readonly note: string | null;
}

function valueOf(profile: PreviewProfile, column: string): string | null {
  const raw =
    column === "phone"
      ? profile.phone
      : column === "email"
        ? profile.email
        : column === "display_name"
          ? profile.display_name
          : null;
  return raw !== null && raw.trim() !== "" ? raw : null;
}

/** Why a column was refused, in words an operator can act on. */
export function skipReasonNote(reason: string | null): string | null {
  switch (reason) {
    case "placeholder_email":
      return "auto-generated address, not a real mailbox";
    case "phone_in_use_by_other_member":
      return "another member already has this number";
    default:
      return null;
  }
}

/**
 * Build the side-by-side identity rows: closing profile on the left, surviving
 * profile on the right, and what the survivor ends up with.
 *
 * The `effect` is derived from the SERVER's verdict, never re-decided here — a
 * `skip` with a value present on the left and nothing on the right is exactly
 * the silent-loss case, and it is labelled `lost` so the UI can shout about it.
 */
export function buildPreviewIdentityRows(
  preview: MergePreview,
): ReadonlyArray<PreviewIdentityRow> {
  return preview.identity.map((verdict) => {
    const absorbedValue = valueOf(preview.absorbed, verdict.column);
    const survivorValue = valueOf(preview.survivor, verdict.column);
    const carries = verdict.decision === "carry";

    let effect: PreviewIdentityRow["effect"];
    if (carries) {
      effect = "moves";
    } else if (survivorValue !== null) {
      effect = "keeps";
    } else if (absorbedValue !== null) {
      effect = "lost";
    } else {
      effect = "neither";
    }

    return {
      column: verdict.column,
      label: identityColumnLabel(verdict.column),
      absorbedValue,
      survivorValue,
      resultValue: carries ? absorbedValue : survivorValue,
      effect,
      note: effect === "lost" ? skipReasonNote(verdict.reason) : null,
    };
  });
}

/** The non-zero move counts, in the order staff care about, ready to render. */
export function buildPreviewMoveRows(
  moves: PreviewMoveCounts,
): ReadonlyArray<{ readonly label: string; readonly count: number }> {
  const rows: ReadonlyArray<readonly [keyof PreviewMoveCounts, string, string]> =
    [
      ["bookings", "booking", "bookings"],
      ["payments", "payment", "payments"],
      ["packages", "package", "packages"],
      ["memberships", "membership", "memberships"],
      ["checkouts", "checkout", "checkouts"],
      ["flows", "marketing flow", "marketing flows"],
      ["tags", "tag", "tags"],
      ["conversations", "conversation", "conversations"],
      ["vendor_links", "linked account", "linked accounts"],
      ["channel_bindings", "chat number", "chat numbers"],
      ["session_notes", "session note", "session notes"],
      ["session_feedback", "feedback entry", "feedback entries"],
      ["picture_intakes", "consultation form photo", "consultation form photos"],
    ];
  return rows.flatMap(([key, one, many]) => {
    const count = moves[key];
    return count > 0 ? [{ label: count === 1 ? one : many, count }] : [];
  });
}

/**
 * The preview's carry notes, in the order the modal shows them under the
 * identity table: the answers the kept profile inherits, the answers where it
 * keeps its own, the ad attribution, then the opt-outs. Empty notes are left out. Built here
 * so the modal only maps over it: which preview fields feed which sentence is
 * pinned by `merge-member-direction.test.ts`, as it could not be inside a
 * component the SPA tree does not test (the pre-push review, 2026-10-14).
 */
export function buildPreviewCarryNotes(
  preview: MergePreview,
  survivorLabel: string,
): ReadonlyArray<{ readonly key: string; readonly text: string }> {
  const answers = describeAnswerCarry({
    carried: preview.answers_carried,
    conflicts: preview.answer_conflicts,
    tense: "preview",
    survivorLabel,
  });
  const notes = [
    { key: "answers-carried", text: answers.carried },
    { key: "answers-kept", text: answers.kept },
    {
      key: "ad-attribution",
      text: describeAdAttributionCarry({
        firstKnownMoved: preview.first_known_moves,
        touchesMoved: preview.attribution_touches_carried,
        tense: "preview",
        survivorLabel,
      }),
    },
    {
      key: "opt-outs",
      text: describeOptOutCarry({
        carried: preview.opt_outs_carried,
        tense: "preview",
        survivorLabel,
      }),
    },
  ];
  return notes.filter((n) => n.text !== "");
}

/**
 * Turn a server warning code into a sentence. Unknown codes degrade to null
 * rather than rendering a raw identifier at an operator.
 */
export function previewWarningText(
  code: string,
  absorbedLabel: string,
  survivorLabel: string,
): string | null {
  switch (code) {
    case "absorbed_has_history":
      return `${absorbedLabel} already has money or bookings on it. Merging moves them to ${survivorLabel}; it is easiest to merge before any exist.`;
    case "phone_in_use_by_other_member":
      return "The phone number cannot be carried over — another member already has it. Resolve that duplicate first if the number should follow.";
    case "placeholder_email_not_carried":
      return "The closing profile's email is an auto-generated placeholder, so it will not be carried over. That is deliberate — it is not a real mailbox.";
    case "survivor_left_without_email":
      return `${survivorLabel} will have no email address after this merge.`;
    case "survivor_left_without_phone":
      return `${survivorLabel} will have no phone number after this merge.`;
    case "direction_may_be_inverted":
      return `Check the direction. ${survivorLabel} looks like the auto-created profile and ${absorbedLabel} like the real account — you may want to swap.`;
    default:
      return null;
  }
}

/** True when the preview contains something the operator should read first. */
export function previewHasSeriousWarning(
  preview: MergePreview,
): boolean {
  return (
    preview.warnings.includes("direction_may_be_inverted") ||
    buildPreviewIdentityRows(preview).some((r) => r.effect === "lost")
  );
}
